import Decimal from 'decimal.js';

import type { ColorsPalette } from 'models/colorsPalette';

import { centsFromDollarsStringOrNull, centsFromDollarsStringOrZero, type DollarsString } from './dollars';
import { numberFromFixedPointString } from './fixedPoint';
import type { PriceLevelStructure } from './priceLevelStructure';
import type { SearchResultMarket } from './search';

/*
  Using union types for defining order statuses.
  Reasoning explained here: https://blog.bam.tech/developer-news/should-you-use-enums-or-union-types-in-typescript
 */
export const marketStatuses = [
    'invalid',
    'initialized',
    'inactive',
    'active',
    'closed',
    'determined',
    'disputed',
    'amended',
    'settled',
    'finalized',
] as const;

export type MarketStatus = (typeof marketStatuses)[number];

export const marketStatusFilters = [
    'open',
    'closed',
    'settled',
    'unopened',
    'open-single',
    'closed-single',
    'settled-single',
    'unopened-single',
];

export const completeMarketStatusFilters = ['open', 'closed', 'settled', 'unopened'];

export type MarketStatusFilter = (typeof marketStatusFilters)[number];

export type MarketResult = '' | 'yes' | 'no' | 'scalar' | 'void' | 'invalid';

export interface SettlementSource {
    url: string;
    name: string;
}

// It can assume multiple property names and values depending on the milestone type
// Stat-ladder (scalar payout) markets carry `scalar_payout_type`, `scalar_floor`,
// `scalar_cap`, `maximum_payout_dollars`, `statistic` and `entity_type` here — see
// `queries/ladderMarket.ts`, which owns their parsing.
export interface CustomStrike {
    [key: string]: string;
}

/** Player/team identity, or the esports competitor for team-scoped esports props. */
export function getCustomStrikeTarget(customStrike: CustomStrike | undefined): string | undefined {
    if (!customStrike) return undefined;
    return (
        Object.entries(customStrike).find(([key]) => key.includes('player') || key.includes('team'))?.[1] ??
        customStrike.esports_competitor
    );
}

/** Structured-target id of the player a strike is about (first `*player*` key holding a target uuid). */
export function getCustomStrikePlayerId(customStrike: CustomStrike | undefined): string | undefined {
    if (!customStrike) return undefined;
    return Object.entries(customStrike).find(
        ([key, value]) => key.toLowerCase().includes('player') && isStructuredTargetUuid(value),
    )?.[1];
}

/** Returns the first customStrike value whose key contains "team". */
export function getCustomStrikeTeam(customStrike: CustomStrike | undefined): string | undefined {
    if (!customStrike) return undefined;
    return Object.entries(customStrike).find(([key]) => key.includes('team'))?.[1];
}

/** Album-scoped strikes are per-release; they should not share milestone-level artist stream live data. */
export function customStrikeHasAlbum(customStrike: CustomStrike | undefined): boolean {
    return !!getCustomStrikeAlbumId(customStrike);
}

/** Returns the album structured target UUID from a custom strike, if present. */
export function getCustomStrikeAlbumId(customStrike: CustomStrike | undefined): string | undefined {
    if (!customStrike) return undefined;
    const album = customStrike.album;
    return typeof album === 'string' && album.length > 0 ? album : undefined;
}

const STRUCTURED_TARGET_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when a custom strike value is a structured-target UUID (vs a numeric strike / plain name). */
export function isStructuredTargetUuid(value: string): boolean {
    return STRUCTURED_TARGET_UUID.test(value);
}

/** Deduped structured-target UUIDs from a market's custom_strike values, in entry order. */
export function getCustomStrikeStructuredTargetIds(customStrike: CustomStrike | undefined): string[] {
    if (!customStrike) return [];
    const ids = Object.values(customStrike).filter(
        (value): value is string => typeof value === 'string' && isStructuredTargetUuid(value),
    );
    return Array.from(new Set(ids));
}

/**
 * Deduped structured-target UUIDs across several markets' custom_strike values.
 * Mirrors `MilestonesStore.loadCustomStrikeStructuredTargets`'s id-collection
 * step — pass the result to `useStructuredTargetsQuery`, which already
 * dedupes/caches by id, so no separate "already cached" filter is needed here.
 */
export function getStructuredTargetIdsFromCustomStrikes(customStrikes: Array<CustomStrike | undefined>): string[] {
    const ids = customStrikes.flatMap((customStrike) => getCustomStrikeStructuredTargetIds(customStrike));
    return Array.from(new Set(ids));
}

/**
 * Returns the structured-target UUID that represents the outcome of a score-based market
 * (NFL, MLB, NBA, etc.) — i.e. the home team ID when home_score > away_score, the away
 * team ID when away_score > home_score, or tie_id when scores are equal.
 * Returns undefined for non-score-based strikes (soccer, esports single-team keys, etc.).
 */
export function getScoreResultTargetId(customStrike: CustomStrike | undefined): string | undefined {
    const homeTeamId = customStrike?.home_team_id;
    const awayTeamId = customStrike?.away_team_id;
    const homeScore = customStrike?.home_score !== undefined ? parseInt(customStrike.home_score, 10) : undefined;
    const awayScore = customStrike?.away_score !== undefined ? parseInt(customStrike.away_score, 10) : undefined;
    if (!homeTeamId || !awayTeamId || homeScore === undefined || awayScore === undefined) return undefined;
    if (isNaN(homeScore) || isNaN(awayScore)) return undefined;
    if (homeScore > awayScore) return homeTeamId;
    if (awayScore > homeScore) return awayTeamId;
    return customStrike?.tie_id;
}

/**
 * Whether a market is the outcome for a structured target (team/competitor). Score-based strikes embed both team
 * UUIDs on every market, so they match by score result; single-key strikes (soccer, esports) match any value.
 */
export function marketVoMatchesTargetId(market: MarketVo, targetId: string): boolean {
    const scoreResultId = getScoreResultTargetId(market.custom_strike);
    if (scoreResultId !== undefined) return scoreResultId === targetId;
    return Object.values(market.custom_strike ?? {}).includes(targetId);
}

export type { PriceLevelStructure };

/**
 * Returns true when the given cents price represents an actionable offer.
 * Prices of 0 (no bid) or 100 (no ask) indicate an empty side of the book — the
 * canonical "no liquidity" sentinels for both YES bid/ask and the NO side
 * derived as `100 - yes_*`.
 */
export const hasLiquidity = (price: number): boolean => price > 0 && price < 100;

export type StrikeType =
    | 'greater'
    | 'greater_or_equal'
    | 'less'
    | 'less_or_equal'
    | 'between'
    | 'structured'
    | 'custom'
    | 'unknown'
    | '';

export interface MveSelectedLeg {
    event_ticker: string;
    market_ticker: string;
    side: 'yes' | 'no';
    event_title: string;
    leg_title: string;
    /** `leg_title` with the event named too — "Over 2.5 goals scored (Arsenal
     * vs Coventry: Total Goals)". Disambiguates legs whose `leg_title` alone
     * repeats within a matchup. */
    leg_title_with_context?: string;
    /** The title to show for the leg under its group header: `leg_title`, with
     * context only where another leg in the same group reads the same. Absent
     * from payloads served before the exchange added it. */
    display_leg_title?: string;
    settlement_value_centicents: number;
    result: MarketResult;
}

/** One matchup inside `mve_selected_legs_by_category` — every leg the combo
 * takes on a single game, under the game's own label ("Arsenal vs Coventry"). */
export interface MveSelectedLegGroup {
    label: string;
    milestone_id?: string;
    color_palettes?: string[];
    legs: MveSelectedLeg[];
}

/** One league inside `mve_selected_legs_by_category` ("EPL", "Pro Baseball",
 * "ATP Cincinnati"), holding its matchups. The backend's own grouping of a
 * combo market's legs — the combo builder renders its panel from it. */
export interface MveSelectedLegCategory {
    label: string;
    category?: string;
    milestone_type?: string;
    groups: MveSelectedLegGroup[];
}

export interface MarketVo {
    colors?: ColorsPalette;
    id: string;
    ticker_name: string;
    title: string;
    sub_title: string;
    yes_sub_title: string;
    description_case_yes?: string;
    description_case_no?: string;

    open_date: string;
    close_date: string;
    /*
     * v2-aligned dates (Kalshi/exchange-infra#29921), RFC3339 and omitted when absent. Read them through
     * `marketLatestExpirationTime` and friends below, which fall back to the legacy names while /v1 still
     * serves them.
     */
    /** The latest the market can expire: max(expiration, close). Replaces `expiration_date`. */
    latest_expiration_time?: string;
    /** When the exchange expects to determine the market, capped at `latest_expiration_time`. */
    expected_expiration_time?: string;
    /** When the outcome occurred; only sent once the market is determined. */
    occurrence_datetime?: string;
    /** When the market was created. Replaces `create_date`. */
    created_time?: string;

    /** Legacy: replaced by `latest_expiration_time`; read through `marketLatestExpirationTime`. */
    expiration_date?: string;
    settlement_timer_seconds: number;
    settlement_value_centicents: number;

    yes_bid_dollars: DollarsString;
    yes_ask_dollars: DollarsString;

    last_price_dollars: DollarsString;
    prev_period_price_dollars?: DollarsString;

    previous_hour_price_dollars?: DollarsString; // last price from the previous hour
    previous_day_price_dollars?: DollarsString; // last price from the previous day
    previous_week_price_dollars?: DollarsString; // last price from the previous week

    /*
     * Volume and open interest in contracts, one side of each trade, at full precision (e.g. "500.00"; fractional
     * contracts are possible). Read them through `marketVolumeContracts` and friends below. Optional until the
     * server ships them (Kalshi/exchange-infra#29569); the helpers fall back to the legacy one-sided integers.
     */
    volume_contracts_fp?: string;
    volume_24h_contracts_fp?: string;
    open_interest_contracts_fp?: string;

    /** Legacy: both sides of every trade, truncated. Only read where a both-sided total is still displayed. */
    volume: number;
    /** Legacy: one side of every trade (`volume / 2`), truncated. Contracts, not dollars. */
    dollar_volume: number;
    /** Legacy: one-sided 24h volume (`recent_volume / 2`), truncated. Contracts, not dollars. */
    dollar_recent_volume: number;
    /** Legacy: both sides, truncated. Also backs the one-sided helper when newer fields are absent. */
    open_interest: number;
    /** Legacy: one-sided open interest (`open_interest / 2`), truncated. Contracts, not dollars. */
    dollar_open_interest?: number;

    status: MarketStatus;
    result: MarketResult;
    expiration_value: string;
    /** Legacy: replaced by `expected_expiration_time` (uncapped); read through `marketExpectedExpirationTime`. */
    expected_expiration_date?: string;
    rulebook_variables: { [variable: string]: string };
    /**
     * The market's own image (Kalshi/exchange-infra#30177). /v1 market payloads have always sent it but
     * with placeholders ("a", "fake_image.com"); nested /v1/events markets gain it with that PR. Read it
     * through `marketImageSource` (utils/marketImage), which falls back to `rulebook_variables.image_link`.
     */
    image_url?: string;
    strike_type?: StrikeType;
    floor_strike?: number;
    cap_strike?: number;

    event_ticker?: string; // Owning event's ticker
    ranged_group_ticker?: string; // Event ticker sent for compatibility

    price_level_structure?: PriceLevelStructure;

    custom_strike?: CustomStrike;
    /** The structured target the market is about (leader/award option markets). */
    structured_target_id?: string;
    /** How this market reads as one leg of a combo — "Frances Tiafoe to win
     * set 1" where the market's own subtitle is the bare "Frances Tiafoe".
     * Self-describing, because a combo lists it away from the event that gives
     * it context. */
    component_leg_title?: string;
    mve_selected_legs?: MveSelectedLeg[];
    mve_selected_legs_by_category?: MveSelectedLegCategory[];
    mve_collection_ticker?: string;

    // Stubbing properties
    /** Legacy: replaced by `created_time`. */
    create_date?: string;
    can_close_early?: boolean;
}

export const mapSearchResultMarketToMarketVo = (market: SearchResultMarket): MarketVo => {
    return {
        id: market.ticker,
        ticker_name: market.ticker,
        title: market.yes_subtitle,
        sub_title: '',
        yes_sub_title: market.yes_subtitle,
        open_date: market.open_ts,
        close_date: market.close_ts,
        latest_expiration_time: market.close_ts,
        settlement_timer_seconds: 0,
        settlement_value_centicents: 0,
        yes_bid_dollars: `${(market.yes_bid / 100).toFixed(4)}`,
        yes_ask_dollars: `${(market.yes_ask / 100).toFixed(4)}`,
        last_price_dollars: `${(market.last_price / 100).toFixed(4)}`,
        volume: 0,
        dollar_volume: 0,
        dollar_recent_volume: 0,
        open_interest: 0,
        status: 'active',
        result: market.result || '',
        expiration_value: '',
        rulebook_variables: market.rulebook_variables,
    };
};

/*
 * Market prices in cents at full precision, read from the `*_dollars` strings. The
 * integer-cent fields the exchange also sends are truncated from centicents, so
 * sub-cent levels (e.g. 99.5¢ on deci-cent markets) are only exact here.
 */
export const marketYesBidCents = (market: Pick<MarketVo, 'yes_bid_dollars'>): number =>
    centsFromDollarsStringOrZero(market.yes_bid_dollars);
export const marketYesAskCents = (market: Pick<MarketVo, 'yes_ask_dollars'>): number =>
    centsFromDollarsStringOrZero(market.yes_ask_dollars);
export const marketLastPriceCents = (market: Pick<MarketVo, 'last_price_dollars'>): number =>
    centsFromDollarsStringOrZero(market.last_price_dollars);
/**
 * [a] minus [b] in cents, computed in decimal so fractional cents don't pick up binary float artifacts
 * (99.7 - 99.6 is 0.1, not 0.10000000000000853).
 */
export const subtractCents = (a: number, b: number): number => new Decimal(a).minus(b).toNumber();
/** The opposite side's price, 100¢ minus [cents] (99.9 -> 0.1). */
export const complementCents = (cents: number): number => subtractCents(100, cents);
/** Last price minus the previous period's price, in cents. */
export const marketLastPriceChangeCents = (
    market: Pick<MarketVo, 'last_price_dollars' | 'prev_period_price_dollars'>,
): number => subtractCents(marketLastPriceCents(market), marketPrevPeriodPriceCents(market) || 0);
/** Midpoint of two cents prices, computed in decimal ((0.1 + 0.2) / 2 is 0.15). */
export const midpointCents = (a: number, b: number): number => new Decimal(a).plus(b).div(2).toNumber();
/**
 * `min(100 - yesAsk, yesBid) - 0.01 * (yesAsk - yesBid)`: favors two-sided books near even with tight spreads.
 * Computed in decimal so mathematically tied books tie exactly in stable sorts.
 */
export const spreadAdjustedBalanceScore = ({ yesBid, yesAsk }: { yesBid: number; yesAsk: number }): number =>
    Decimal.min(new Decimal(100).minus(yesAsk), yesBid).minus(new Decimal(yesAsk).minus(yesBid).times(0.01)).toNumber();
/**
 * Mirrors `BinaryMarket.controversy`: higher means more contested. Mutually exclusive events rank the favorite
 * first (highest yes bid); independent outcomes rank the most balanced book first. Computed in decimal so books
 * that tie mathematically (e.g. mirrored 0.1/0.2c and 99.8/99.9c) tie exactly and stable sorts keep their order.
 * Callers short-circuit determined markets to 0.
 */
export const controversyScore = (
    { yesBid, yesAsk, lastPrice }: { yesBid: number; yesAsk: number; lastPrice: number },
    mutuallyExclusive: boolean,
): number => {
    // A missing price made the original float formula NaN; keep that rather than throwing.
    if (![yesBid, yesAsk, lastPrice].every(Number.isFinite)) return NaN;
    const bid = new Decimal(yesBid);
    const ask = new Decimal(yesAsk);
    // Reward for tight spreads
    const spreadPenalty = ask.minus(bid).times(0.01);
    if (mutuallyExclusive) {
        return bid.minus(spreadPenalty).minus(new Decimal(100).minus(lastPrice).abs().times(0.01)).toNumber();
    }
    return (
        Decimal.min(new Decimal(100).minus(ask), bid)
            .minus(spreadPenalty)
            // Tiebreaker for balance
            .plus(new Decimal(50).minus(lastPrice).abs().times(0.01))
            .minus(yesBid === 0 ? 50 : 0)
            .minus(yesAsk === 100 ? 50 : 0)
            .toNumber()
    );
};
/** Historical prices; `undefined` when the exchange omits the field. */
export const marketPrevPeriodPriceCents = (market: Pick<MarketVo, 'prev_period_price_dollars'>): number | undefined =>
    centsFromDollarsStringOrNull(market.prev_period_price_dollars) ?? undefined;
export const marketPreviousHourPriceCents = (
    market: Pick<MarketVo, 'previous_hour_price_dollars'>,
): number | undefined => centsFromDollarsStringOrNull(market.previous_hour_price_dollars) ?? undefined;
export const marketPreviousDayPriceCents = (market: Pick<MarketVo, 'previous_day_price_dollars'>): number | undefined =>
    centsFromDollarsStringOrNull(market.previous_day_price_dollars) ?? undefined;
export const marketPreviousWeekPriceCents = (
    market: Pick<MarketVo, 'previous_week_price_dollars'>,
): number | undefined => centsFromDollarsStringOrNull(market.previous_week_price_dollars) ?? undefined;

/** A one-sided contract count: the fixed-point string when the server sends it, else the legacy one-sided integer. */
const contractsOrLegacy = (fixedPoint: string | undefined, legacyOneSided: number | undefined): number =>
    fixedPoint !== undefined && fixedPoint !== '' ? numberFromFixedPointString(fixedPoint) : (legacyOneSided ?? 0);
/** Lifetime volume in contracts, one side of each trade. */
export const marketVolumeContracts = (
    market: Partial<Pick<MarketVo, 'volume_contracts_fp' | 'dollar_volume'>>,
): number => contractsOrLegacy(market.volume_contracts_fp, market.dollar_volume);
/** Trailing-24h volume in contracts, one side of each trade. */
export const marketVolume24hContracts = (
    market: Partial<Pick<MarketVo, 'volume_24h_contracts_fp' | 'dollar_recent_volume'>>,
): number => contractsOrLegacy(market.volume_24h_contracts_fp, market.dollar_recent_volume);
/** One-sided open interest, falling back to half the required legacy count if the optional field is absent. */
export const marketOpenInterestContracts = (
    market: Partial<Pick<MarketVo, 'open_interest_contracts_fp' | 'dollar_open_interest' | 'open_interest'>>,
): number =>
    contractsOrLegacy(
        market.open_interest_contracts_fp,
        market.dollar_open_interest ?? (market.open_interest ?? 0) / 2,
    );
/** Sums contract counts in decimal, so fractional contracts don't pick up binary float artifacts (0.1 + 0.2 is 0.3). */
export const sumContracts = (counts: Iterable<number>): number => {
    let total = new Decimal(0);
    for (const count of counts) total = total.plus(count);
    return total.toNumber();
};

/*
 * Market dates under their v2-aligned names, falling back to the legacy /v1 names while the server still
 * sends both. Empty strings count as absent; `undefined` means the market carries neither.
 */
type MarketDateFields = Partial<
    Pick<
        MarketVo,
        | 'latest_expiration_time'
        | 'expiration_date'
        | 'expected_expiration_time'
        | 'expected_expiration_date'
        | 'occurrence_datetime'
    >
>;

/** The latest the market can expire, max(expiration, close). */
export const marketLatestExpirationTime = (
    market: Pick<MarketDateFields, 'latest_expiration_time' | 'expiration_date'>,
): string | undefined => market.latest_expiration_time || market.expiration_date || undefined;

/**
 * When the exchange expects to determine the market, never later than {@link marketLatestExpirationTime}.
 * The server caps `expected_expiration_time` itself; the legacy `expected_expiration_date` is not capped, so
 * the cap is applied here too and both sources read the same.
 */
export const marketExpectedExpirationTime = (
    market: Pick<
        MarketDateFields,
        'expected_expiration_time' | 'expected_expiration_date' | 'latest_expiration_time' | 'expiration_date'
    >,
): string | undefined => {
    const expected = market.expected_expiration_time || market.expected_expiration_date || undefined;
    if (!expected) return undefined;
    const latest = marketLatestExpirationTime(market);
    if (!latest) return expected;
    const expectedMs = Date.parse(expected);
    const latestMs = Date.parse(latest);
    return Number.isFinite(expectedMs) && Number.isFinite(latestMs) && latestMs < expectedMs ? latest : expected;
};

/** When the outcome occurred. Only set once the market is determined. */
export const marketOccurrenceDatetime = (
    market: Pick<MarketDateFields, 'occurrence_datetime'> & { event_occurrence_datetime?: string | null },
): string | undefined => market.occurrence_datetime || market.event_occurrence_datetime || undefined;
